import { Link } from "react-router-dom";
import {
  ShieldCheck,
  HeartHandshake,
  Swords,
  Wind,
  BookOpenCheck,
  Sparkles,
  School,
  Users,
  ArrowUpRight,
  ExternalLink,
  Quote,
} from "lucide-react";
import { FadeUp, MaskedLines, Overline } from "@/components/Reveal";

const VALUES = [
  { id: "value-respect", kanji: "礼", name: "Respect", text: "Bullying-ul se destramă atunci când respectul devine normă, nu excepție." },
  { id: "value-curaj", kanji: "勇", name: "Curaj", text: "Curajul de a rămâne tu însuți într-o lume care te împinge să fii altcineva." },
  { id: "value-integritate", kanji: "誠", name: "Integritate", text: "Promisiunea tăcută de a nu te trăda pe tine și de a nu-i trăda pe ceilalți." },
  { id: "value-compasiune", kanji: "仁", name: "Compasiune", text: "Forța care vindecă și unește: să vezi dincolo de aparențe și să răspunzi cu empatie." },
  { id: "value-disciplina", kanji: "克", name: "Disciplină", text: "Obiceiuri zilnice care dau stabilitate — iar stabilitatea reduce agresivitatea." },
  { id: "value-onoare", kanji: "名誉", name: "Onoare", text: "Asumarea responsabilității pentru propriile acțiuni și alegerea binelui." },
];

const PILLARS = [
  {
    id: "pillar-1",
    icon: Wind,
    title: "Mushin · mintea limpede",
    text: "Respirație conștientă și prezență: spațiul dintre frică și acțiune. Copiii învață să nu fie copleșiți de emoții, ci să le înțeleagă.",
  },
  {
    id: "pillar-2",
    icon: ShieldCheck,
    title: "Putere interioară, nu forță",
    text: "Adevăratul războinic nu rănește, ci apără. Transformăm victime și agresori în lideri ai propriei vieți.",
  },
  {
    id: "pillar-3",
    icon: HeartHandshake,
    title: "Comunități empatice",
    text: "În loc de excludere și competiție toxică, grupul devine spațiu de sprijin reciproc — în clasă, pe teren, la festival.",
  },
  {
    id: "pillar-4",
    icon: BookOpenCheck,
    title: "Practică, nu teorie",
    text: "Jurnalul Bushido, ritualul recunoștinței, momentul de tăcere, antrenamentul zilnic: exerciții simple, repetate.",
  },
];

const STEPS = [
  {
    nr: "01",
    title: "Atelier de deschidere în școală",
    text: "O întâlnire de 90 de minute cu o clasă sau un an de studiu: ce este bullying-ul, ce este puterea interioară, ce este Bushido.",
  },
  {
    nr: "02",
    title: "Cele șase virtuți, pe rând",
    text: "Sesiuni scurte, una pentru fiecare valoare — respect, curaj, integritate, compasiune, disciplină, onoare — cu exerciții și discuții deschise.",
  },
  {
    nr: "03",
    title: "Antrenamentul minții",
    text: "Respirație conștientă, momente de tăcere și jurnal Bushido, integrate în rutina săptămânală a clasei.",
  },
  {
    nr: "04",
    title: "Mentori din rândul elevilor",
    text: "Elevii care duc mai departe metoda devin mentori pentru colegii mai mici — protectori, nu spectatori.",
  },
  {
    nr: "05",
    title: "Evenimentele ca teren de aplicare",
    text: "La RISE 27' și la mini-campionatul caritabil, valorile se testează în afara clasei: fair-play, incluziune, respect între echipe și public.",
  },
  {
    nr: "06",
    title: "Sprijin pentru profesori și părinți",
    text: "Ghid, materiale și sesiuni dedicate adulților: cum se cultivă respectul și cum se gestionează conflictele fără pedeapsă și fără etichete.",
  },
];

const BENEFITS = [
  "Reducerea bullying-ului prin respect și curaj cultivate zilnic",
  "Stabilitate emoțională: mai puțină anxietate, mai multă claritate",
  "Încredere în sine fără agresivitate și fără nevoia de a domina",
  "Relații sănătoase între elevi, profesori și părinți",
  "Copii care își asumă responsabilitatea propriilor acțiuni",
  "Pregătire pentru viață: demnitate și echilibru în fața provocărilor",
];

const EVENTS = [
  {
    id: "event-rise27",
    to: "/rise27",
    tag: "Festival · 2027",
    title: "RISE 27'",
    text: "Muzica ascultată de tineri + istoria cetății, pe fundalul aceluiași mesaj: respect, incluziune, zero bullying.",
  },
  {
    id: "event-cupa",
    to: "/cupa-caritabila",
    tag: "Sport caritabil · 2026",
    title: "Mini-campionat de fotbal",
    text: "Fair-play, echipe mixte și fonduri pentru proiectele noastre — sportul ca antrenament al caracterului.",
  },
];

const Hero = () => (
  <section data-testid="bushido-hero" className="relative overflow-hidden border-b border-white/10">
    <div className="absolute inset-0 bg-[#07090d]" />
    <div
      aria-hidden="true"
      className="absolute -right-24 top-10 select-none font-display text-[22rem] font-black leading-none text-white/[0.03] sm:text-[30rem]"
    >
      武
    </div>
    <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-36 sm:px-8 lg:px-12 lg:pb-28 lg:pt-44">
      <Overline>Proiect-umbrelă · Asociația Art &amp; Outdoor România</Overline>
      <h1
        data-testid="bushido-page-title"
        className="mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl"
      >
        <MaskedLines
          lines={[
            <>Bushido</>,
            <span key="l2" className="text-neon">vs. Bullying</span>,
          ]}
        />
      </h1>
      <FadeUp delay={0.5}>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg">
          Transformăm frica în prietenie și respect. O metodă nouă de prevenire a
          bullying-ului și a discriminării între copii, inspirată din codul
          Bushido — calea războinicilor samurai: nu despre luptă, ci despre
          stăpânire de sine.
        </p>
      </FadeUp>
      <FadeUp delay={0.62}>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-neutral-400">
          Acesta este scopul principal al tuturor evenimentelor noastre. Fiecare
          festival, turneu sau atelier promovează aceleași valori în rândul
          tinerilor și în școli.
        </p>
      </FadeUp>
      <FadeUp delay={0.75} className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/contact"
          data-testid="bushido-school-cta"
          className="group inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
        >
          <School className="h-4 w-4" /> Adu programul în școala ta
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <a
          href="https://www.scoalasamurai.com"
          target="_blank"
          rel="noreferrer"
          data-testid="bushido-external-link"
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-neon hover:text-neon"
        >
          <ExternalLink className="h-4 w-4" /> Școala Samurai
        </a>
      </FadeUp>
    </div>
  </section>
);

const Problem = () => (
  <section data-testid="bushido-problem-section" className="bg-surface">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <FadeUp className="lg:col-span-5">
          <Overline>Provocarea prezentului</Overline>
          <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
            Bullying-ul nu e un caz <span className="text-neon">izolat</span>
          </h2>
        </FadeUp>
        <div className="space-y-6 lg:col-span-7">
          <FadeUp>
            <p className="text-base leading-relaxed text-neutral-300">
              Agresiunea verbală, excluderea socială și violența emoțională lasă
              urme adânci: slăbesc încrederea în sine și capacitatea de a
              construi relații sănătoase. Tinerii cresc într-un mediu unde
              presiunea de a se conforma, competiția nesănătoasă și lipsa de
              empatie îi fac vulnerabili.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="text-base leading-relaxed text-neutral-400">
              Răspunsul nostru nu este o campanie de afișe. Este o mentalitate:
              tinerii nu trebuie să devină mai duri, ci mai conștienți. Nu
              trebuie să lupte cu ceilalți, ci cu propriile frici. Nu trebuie să
              se conformeze, ci să se descopere.
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="flex gap-4 rounded-2xl border-l-2 border-neon bg-carddark p-6">
              <Quote className="h-5 w-5 shrink-0 text-neon" />
              <p className="text-sm italic leading-relaxed text-neutral-300">
                „Controlul asupra propriei tale vieți începe cu controlul asupra
                propriei tale minți.” — Miyamoto Musashi
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
);

const Values = () => (
  <section data-testid="bushido-values-section" className="border-t border-white/10 bg-canvas">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
      <FadeUp>
        <Overline>Codul Bushido</Overline>
        <h2 className="mt-4 max-w-2xl font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
          Șase valori, <span className="text-neon">un antidot</span>
        </h2>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {VALUES.map((v, i) => (
          <FadeUp key={v.id} delay={i * 0.06} className="bg-canvas">
            <div data-testid={v.id} className="group h-full px-7 py-10 transition-colors duration-500 hover:bg-surface">
              <span className="font-display text-3xl font-black text-neon/70 transition-colors duration-500 group-hover:text-neon">
                {v.kanji}
              </span>
              <h3 className="mt-5 font-display text-lg font-black uppercase tracking-tight text-white">
                {v.name}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400">{v.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section data-testid="bushido-method-section" className="border-t border-white/10 bg-surface">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
      <FadeUp>
        <Overline>Metoda</Overline>
        <h2 className="mt-4 max-w-2xl font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
          Cum <span className="text-neon">funcționează</span>
        </h2>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
        {PILLARS.map(({ icon: Icon, ...p }, i) => (
          <FadeUp key={p.id} delay={i * 0.08}>
            <div
              data-testid={p.id}
              className={`h-full rounded-3xl border border-white/10 bg-carddark p-8 transition-colors duration-500 hover:border-neon/50 ${
                i % 2 === 1 ? "md:mt-10" : ""
              }`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-canvas text-neon">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-6 font-display text-xl font-black uppercase tracking-tight text-white">
                {p.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-neutral-400">{p.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section data-testid="bushido-steps-section" className="border-t border-white/10 bg-canvas">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
      <FadeUp>
        <Overline>Programul în școli</Overline>
        <h2 className="mt-4 max-w-2xl font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
          Șase pași, <span className="text-neon">un an școlar</span>
        </h2>
      </FadeUp>
      <div className="mt-14 space-y-px bg-white/10">
        {STEPS.map((s, i) => (
          <FadeUp key={s.nr} delay={i * 0.05} className="bg-canvas">
            <div
              data-testid={`bushido-step-${s.nr}`}
              className="group grid grid-cols-1 gap-4 py-8 transition-colors duration-500 hover:bg-surface md:grid-cols-12 md:gap-8"
            >
              <span className="font-mono text-sm font-bold text-flame md:col-span-1">{s.nr}</span>
              <h3 className="font-display text-lg font-black uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-neon md:col-span-4">
                {s.title}
              </h3>
              <p className="text-sm leading-relaxed text-neutral-400 md:col-span-7">{s.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Benefits = () => (
  <section data-testid="bushido-benefits-section" className="border-t border-white/10 bg-surface">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <FadeUp className="lg:col-span-5">
          <Overline>Ce se schimbă</Overline>
          <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
            O nouă mentalitate pentru o <span className="text-neon">nouă generație</span>
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-neutral-400">
            Tinerii nu au nevoie de armuri mai groase, ci de o înțelegere mai
            profundă a lumii lor interioare.
          </p>
        </FadeUp>
        <div className="lg:col-span-7">
          <div className="space-y-px bg-white/10">
            {BENEFITS.map((b, i) => (
              <FadeUp key={b} delay={i * 0.06} className="bg-surface">
                <div className="flex items-start gap-4 py-5">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-neon" />
                  <p className="text-sm leading-relaxed text-neutral-300">{b}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Events = () => (
  <section data-testid="bushido-events-section" className="border-t border-white/10 bg-canvas">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
      <FadeUp>
        <Overline>Evenimentele din cadrul proiectului</Overline>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
          Fiecare eveniment duce <span className="text-neon">același mesaj</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-400">
          Festivalul, turneul de fotbal și atelierele au scopuri proprii —
          muzică, sport, istorie, strângere de fonduri — dar toate promovează
          respectul, incluziunea și zero toleranță la bullying și discriminare.
        </p>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
        {EVENTS.map((e, i) => (
          <FadeUp key={e.id} delay={i * 0.1}>
            <Link
              to={e.to}
              data-testid={`bushido-${e.id}`}
              className="group block h-full rounded-3xl border border-white/10 bg-surface p-8 transition-colors duration-500 hover:border-neon/50"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-flame">{e.tag}</p>
              <h3 className="mt-4 flex items-center gap-2 font-display text-2xl font-black uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-neon">
                {e.title}
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-neutral-400">{e.text}</p>
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Cta = () => (
  <section data-testid="bushido-cta-section" className="border-t border-white/10 bg-surface">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-28">
      <FadeUp>
        <div className="rounded-3xl border border-neon/25 bg-canvas p-8 sm:p-12">
          <Swords className="h-6 w-6 text-neon" />
          <h2 className="mt-6 max-w-3xl font-display text-2xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
            Ești profesor, director sau părinte?
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400">
            Aducem programul în clasa sau în școala ta. Scrie-ne și stabilim
            împreună un prim atelier — fără costuri pentru elevi.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="bushido-contact-cta"
              className="inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
            >
              <Users className="h-4 w-4" /> Scrie-ne
            </Link>
            <Link
              to="/proiecte"
              data-testid="bushido-projects-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-neon hover:text-neon"
            >
              Vezi toate proiectele
            </Link>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function Bushido() {
  return (
    <>
      <Hero />
      <Problem />
      <Values />
      <Pillars />
      <Steps />
      <Benefits />
      <Events />
      <Cta />
    </>
  );
}
